import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Layout from "../../../layout/default";
import Block from "../../../components/Block/Block";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Icon } from "../../../components";
import { useState, useEffect } from "react";
import api from "../../../../src/services/auth/api";

function CategoryEdit() {
  const { id } = useParams(); // Get category ID from URL params
  const [data, setData] = useState({}); // State for category data
  const [loading, setLoading] = useState(false); // Loading state
  const [validated, setValidated] = useState(false); // Validation state

  // Handle form input changes
  const handleInputs = (e) => {
    const { name, value, files } = e.target;
  
    if (files && files.length > 0) {
      // Handle file input
      setData({ ...data, [name]: files[0] });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  // Handle form submission
  const postData = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      setValidated(true);
    } else {
      event.preventDefault();
  
      const formData = new FormData();
  
      // Append all data fields to FormData
      Object.keys(data).forEach((key) => {
        if (key === "image" && data[key] instanceof File) {
          formData.append(key, data[key]); // Attach the file
        } else {
          formData.append(key, data[key]);
        }
      });
  
      // Send PUT request to update category
      api
        .post(`${process.env.REACT_APP_HOST}api/categoryupdate/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
        })
        .then((response) => {
          if (response.data.success) {
            updateSuccess();
            setValidated(false);
          } else {
            updateError(response.data.content.error_description);
          }
        })
        .catch((err) => {
          if (err.response && err.response.data.validationErrors) {
            updateError(err.response.data.validationErrors);
          }
        });
  
      setValidated(true);
    }
  };

  // Clear form data
  const clear = () => {
    setData({
      title: "",
      description: "",
      image_url: "",
    });
  };

  // Fetch category data from API
  const getIdList = () => {
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_HOST}api/category/${id}`)
      .then((response) => {
        if (response.data.success) {
          setData(response.data.data);
        }
      })
      .catch((err) => {
        const message = err.response.data.errorMessages[0].message[0].message;
        setData({});
        editError(message);
      })
      .finally(() => setLoading(false)); // Ensure loading is set to false
  };

  useEffect(() => {
    getIdList();
  }, [id]);

  const navigate = useNavigate();

  // Success message after update
  const updateSuccess = () => {
    Swal.fire({
      icon: "success",
      title: "Updated successfully",
    }).then(() => navigate("/shop/admin/Category-list"));
  };

  // Error message after failed update
  const updateError = (message) => {
    let errorMessage;
    if (typeof message === "object") {
      errorMessage = Object.values(message).join("<br>");
    } else {
      errorMessage = message;
    }
    Swal.fire({
      icon: "error",
      title: "Save attempt was not successful",
      html: errorMessage,
    });
  };

  // Edit error message
  const editError = (message) => {
    Swal.fire({
      icon: "error",
      title: message,
      text: "Something went wrong!",
    }).then(() => navigate("#"));
  };

  return (
    <Layout title="Edit Category">
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Edit Category</Block.Title>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex">
              <li>
                <Link
                  to="/shop/admin/Category-list"
                  className="btn btn-primary btn-md d-md-none"
                >
                  <Icon name="arrow-long-left" />
                  <span>Go to List</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/shop/admin/Category-list"
                  className="btn btn-primary d-none d-md-inline-flex"
                >
                  <Icon name="arrow-long-left" />
                  <span>Go to List</span>
                </Link>
              </li>
            </ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block className="mt-n5">
        <Form noValidate validated={validated} onSubmit={postData}>
          <Row className="g-3 ">
            <Card>
              <Card.Body>
                {loading ? (
                  <h1 className="d-flex justify-content-center align-items-center">
                    Loading...
                  </h1>
                ) : (
                  <Row className="g-gs">
                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="title">
                          Category Title<span className="text-danger">*</span>
                        </Form.Label>
                        <div className="form-control-wrap">
                          <Form.Control
                            id="title"
                            name="title"
                            value={data.title || ""}
                            onChange={handleInputs}
                            type="text"
                            placeholder="Enter Category"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Category Name is required.
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="description">
                          Category Description <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="form-control-wrap">
                          <Form.Control
                            id="description"
                            name="description"
                            value={data.description || ""}
                            onChange={handleInputs}
                            type="text"
                            placeholder="Enter Category Description"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Category Description is required.
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="image">
                          Category Image
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="form-control-wrap">
                          <Form.Control
                            id="image"
                            name="image"
                            type="file"
                            onChange={handleInputs}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Category Image is required.
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="gap-col">
                  <ul className="d-flex align-items-center justify-content-center gap g-3">
                    <li>
                      <Button type="submit" variant="primary">
                        Update
                      </Button>
                    </li>
                    <li>
                      <Button type="button" variant="secondary" onClick={clear}>
                        Cancel
                      </Button>
                    </li>
                  </ul>
                </div>
              </Card.Body>
            </Card>
          </Row>
        </Form>
      </Block>
    </Layout>
  );
}

export default CategoryEdit;
